import { DateTime, IANAZone } from "luxon";

import { LocalStorageConfigs, saveToLocal } from "../types/LocalStorage";
import { ICachedScheduleSlot, ICalendarService, ScheduleSlot } from "../Calendar/types/ICalendar";
import ICenter, { Center } from "./ICenter";
import { BaseReservation, ICachedReservationCenter } from "./IReservation";

/** A primitive representation of a booking reservation cached to the browser. */
export interface ICachedReservation {
  /** {@inheritdoc ICachedReservationCenter} */
  center: ICachedReservationCenter;
  /** The cached slot of the reservation. */
  slot: ICachedScheduleSlot;
  /** {@inheritdoc IReservation.expirationUtc} */
  expirationUtc: string;
  /** {@inheritdoc IReservation.service} */
  service: ICalendarService;
}

/** A {@link ScheduleSlot} restored from the browser cache. */
export class CachedSlot extends ScheduleSlot {
  constructor(cachedSlot: ICachedScheduleSlot) {
    const zone = new IANAZone(cachedSlot.timeZoneName);
    if (!zone.isValid) throw new Error("Invalid IANA zone name.");
    super(cachedSlot.id, cachedSlot.startTimeUtcIso, cachedSlot.endTimeUtcIso, cachedSlot.isOccupied, zone);
  }
}

/** A reservation holding a scheduled slot for booking confirmation. */
class Reservation extends BaseReservation {
  /** {@inheritdoc Reservation.slot} */
  readonly slot: ScheduleSlot;

  constructor(center: ICenter, service: ICalendarService, slot: ScheduleSlot, expiresUtc?: DateTime) {
    super(center, slot, service, expiresUtc);
    this.slot = slot;
  }

  /** {@inheritdoc ICached} */
  override cache = () => {
    const cachedReservation: ICachedReservation = {
      center: this.center.toPrimitive(),
      slot: {
        id: this.slot.id,
        startTimeUtcIso: this.slot.startTimeUtc.toISO(),
        endTimeUtcIso: this.slot.endTimeUtc.toISO(),
        timeZoneName: this.slot.startTimeLocal.zone.name,
        isOccupied: this.slot.isOccupied
      },
      expirationUtc: this.expirationUtc.toISO(),
      service: this.service
    };
    saveToLocal(cachedReservation, LocalStorageConfigs.reservation.key);
  };

  /** {@inheritdoc IVolatileCache.unsetCache} */
  override unsetCache = () => {
    localStorage.removeItem(LocalStorageConfigs.reservation.key);
    localStorage.removeItem(LocalStorageConfigs.legacy.reservation.key);
  };
}

export class CachedReservation extends Reservation {
  constructor(cachedReservation: ICachedReservation) {
    const zone = new IANAZone(cachedReservation.center.timeZoneName);
    if (!zone.isValid) throw new Error("Invalid IANA zone name.");
    super(
      new Center(
        cachedReservation.center.id,
        cachedReservation.center.distance,
        cachedReservation.center.name,
        cachedReservation.center.address,
        cachedReservation.center.phoneNumber,
        zone
      ),
      cachedReservation.service,
      new CachedSlot(cachedReservation.slot),
      DateTime.fromISO(cachedReservation.expirationUtc).toUTC()
    );
  }
}

export default Reservation;
